/**
 * workspaceTemplates.js
 *
 * Per-role starter workspaces for the workspace dashboard. The admin's global
 * dashboard ships its templates client-side (there is no template endpoint in
 * the backend), so the phone does the same: curated lists of catalogue ids, each
 * sized for a phone. Widgets of modules the user cannot access are filtered at
 * apply time (`buildWidgets({isAllowed})`), so a template never places a card that
 * would only show "locked".
 *
 * Titles/descriptions are app locale keys (`wsd_tpl_<id>` / `wsd_tpl_<id>_desc`).
 *
 * @module helpers/workspaces/workspaceTemplates
 */

const w = (type, size) => (size ? {type, size} : {type});

/**
 * @typedef {object} WorkspaceTemplate
 * @property {string} id           Template id.
 * @property {string} icon         MDI glyph.
 * @property {string} tone         Tone key for the chip.
 * @property {string[]} profiles   Dashboard profiles (`resolveDashboardProfile`) it is suggested for.
 * @property {Array<{type: string, size?: string}>} widgets Catalogue widgets, in order.
 */

/** @type {WorkspaceTemplate[]} */
export const WORKSPACE_TEMPLATES = [
    {
        id: 'manager',
        icon: 'briefcase-outline',
        tone: 'primary',
        profiles: ['hospitality'],
        widgets: [
            w('action_center'),
            w('sales_hero_revenue', 'S'),
            w('finance_hero_net', 'S'),
            w('crm_hero_leads', 'S'),
            w('service_hero_revenue', 'S'),
            w('sales_revenue_trend'),
            w('crm_leads_by_status'),
            w('finance_overdue_receivables'),
            w('stock_low_items'),
        ],
    },
    {
        id: 'sales',
        icon: 'account-tie-outline',
        tone: 'violet',
        profiles: ['crm'],
        widgets: [
            w('crm_overview'),
            w('crm_hero_leads', 'S'),
            w('crm_new_customers', 'S'),
            w('crm_leads_trend'),
            w('crm_leads_by_status'),
            w('crm_follow_ups'),
            w('crm_expiring_quotes'),
            w('crm_owner_leaderboard'),
        ],
    },
    {
        id: 'service',
        icon: 'wrench-outline',
        tone: 'info',
        profiles: ['service'],
        widgets: [
            w('service_overview'),
            w('service_hero_revenue', 'S'),
            w('calendar_hero_events', 'S'),
            w('service_orders_trend'),
            w('service_orders_by_status'),
            w('service_overdue_orders'),
            w('service_today_timeline'),
            w('service_mechanic_workload'),
        ],
    },
    {
        id: 'warehouse',
        icon: 'warehouse',
        tone: 'warning',
        profiles: ['warehouse'],
        widgets: [
            w('stock_overview'),
            w('stock_hero_value', 'S'),
            w('procurement_hero_spend', 'S'),
            w('stock_health'),
            w('stock_low_items'),
            w('stock_expiring_lots'),
            w('stock_receipts_trend'),
            w('procurement_awaiting_approval'),
            w('procurement_overdue_deliveries'),
        ],
    },
    {
        id: 'driver',
        icon: 'truck-outline',
        tone: 'success',
        profiles: [],
        widgets: [
            w('calendar_today'),
            w('calendar_hero_events', 'S'),
            w('fleet_hero_fuel', 'S'),
            w('fleet_overview'),
            w('fleet_maintenance_due'),
            w('fleet_expiring_documents'),
            w('fleet_trips_trend'),
            w('fleet_fuel_trend'),
        ],
    },
    {
        id: 'finance',
        icon: 'bank-outline',
        tone: 'success',
        profiles: ['finance'],
        widgets: [
            w('finance_overview'),
            w('finance_hero_net', 'S'),
            w('sales_hero_revenue', 'S'),
            w('finance_cashflow'),
            w('finance_overdue_receivables'),
            w('finance_payables_due'),
            w('finance_receivables_aging'),
            w('finance_expense_categories'),
            w('finance_actions'),
        ],
    },
];

/**
 * @param {string} id Template id.
 * @returns {WorkspaceTemplate|undefined} The template.
 */
export const getWorkspaceTemplate = (id) => WORKSPACE_TEMPLATES.find((template) => template.id === id);

/**
 * Order templates so the one matching the user's dashboard profile comes first
 * (`manager` otherwise), without hiding any of them.
 *
 * @param {?string} profile Result of `resolveDashboardProfile`.
 * @returns {WorkspaceTemplate[]} Templates, suggested first.
 */
export const orderTemplatesForProfile = (profile) => {
    const suggested = WORKSPACE_TEMPLATES.filter((template) => template.profiles.includes(profile));
    const head = suggested.length ? suggested : WORKSPACE_TEMPLATES.filter((template) => template.id === 'manager');

    return [...head, ...WORKSPACE_TEMPLATES.filter((template) => !head.includes(template))];
};

/**
 * Translation keys of a template.
 *
 * @param {string} id Template id.
 * @returns {{title: string, description: string}} Locale keys.
 */
export const templateKeys = (id) => ({title: `wsd_tpl_${id}`, description: `wsd_tpl_${id}_desc`});

export default WORKSPACE_TEMPLATES;
